import React, { useState, useEffect, useCallback, Component } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { CommentaryPanelSkeleton } from '../../../components/ui/SkeletonLoader';

// ── Error Boundary ────────────────────────────────────────────────────────────
class CommentariesErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('CommentariesVerseInsights error boundary caught:', error, info);
  }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-600 mb-2">
            <Icon name="AlertCircle" size={15} />
            <span className="text-sm font-medium">Commentaries panel encountered an error</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => this.setState({ hasError: false })}
            className="text-amber-500 border-amber-500/30 hover:bg-amber-500/10"
          >
            <Icon name="RefreshCw" size={13} className="mr-1" />
            Retry
          </Button>
        </div>
      );
    }
    return this.props?.children;
  }
}

const COMMENTATORS = [
  { id: 'barnes', label: 'Albert Barnes' },
  { id: 'benson', label: 'Joseph Benson' },
  { id: 'gill', label: 'John Gill' },
  { id: 'clarke', label: 'Adam Clarke' },
  { id: 'wesley', label: 'John Wesley' },
  { id: 'henry', label: 'Matthew Henry' },
  { id: 'cambridge', label: 'Cambridge Bible' },
  { id: 'pulpit', label: 'Pulpit Commentary' },
];

// Scopes in display order: Book, Chapter, Verse
const SCOPES = [
  { id: 'book', label: 'Book' },
  { id: 'chapter', label: 'Chapter' },
  { id: 'verse', label: 'Verse' },
];

// Decode HTML entities including numeric entities like &#1488; for Hebrew/Greek
const decodeHTMLEntities = (text) => {
  if (!text) return text;
  return text?.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(parseInt(dec, 10)))?.replace(/&#x([0-9a-fA-F]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))?.replace(/&amp;/g, '&')?.replace(/&lt;/g, '<')?.replace(/&gt;/g, '>')?.replace(/&quot;/g, '"')?.replace(/&apos;/g, "'")?.replace(/&nbsp;/g, '\u00A0');
};

// ── Abbreviation expansion map (covers Mt, Mr, Lu, Jno and more) ─────────────
const BOOK_ABBREV_INLINE = {
  'Mt': 'Matthew', 'Matt': 'Matthew',
  'Mr': 'Mark', 'Mk': 'Mark', 'Mar': 'Mark',
  'Lu': 'Luke', 'Lk': 'Luke',
  'Jno': 'John', 'Jn': 'John',
  'Ac': 'Acts', 'Act': 'Acts',
  'Ro': 'Romans', 'Rom': 'Romans',
  'Ga': 'Galatians', 'Gal': 'Galatians',
  'Eph': 'Ephesians',
  'Php': 'Philippians', 'Phil': 'Philippians',
  'Col': 'Colossians',
  'He': 'Hebrews', 'Heb': 'Hebrews',
  'Jas': 'James',
  'Re': 'Revelation', 'Rev': 'Revelation',
  'Ps': 'Psalms', 'Psa': 'Psalms',
  'Pr': 'Proverbs', 'Pro': 'Proverbs', 'Prov': 'Proverbs',
  'Isa': 'Isaiah', 'Is': 'Isaiah',
  'Jer': 'Jeremiah', 'Je': 'Jeremiah',
  'Ge': 'Genesis', 'Gen': 'Genesis',
  'Ex': 'Exodus', 'Exo': 'Exodus',
  'De': 'Deuteronomy', 'Deu': 'Deuteronomy', 'Deut': 'Deuteronomy',
  'Nu': 'Numbers', 'Num': 'Numbers',
  'Le': 'Leviticus', 'Lev': 'Leviticus',
  'Da': 'Daniel', 'Dan': 'Daniel',
  'Ho': 'Hosea', 'Hos': 'Hosea',
  'Am': 'Amos',
  'Mi': 'Micah', 'Mic': 'Micah',
  'Na': 'Nahum', 'Nah': 'Nahum',
  'Hab': 'Habakkuk',
  'Zep': 'Zephaniah', 'Zeph': 'Zephaniah',
  'Hag': 'Haggai',
  'Zec': 'Zechariah', 'Zech': 'Zechariah',
  'Mal': 'Malachi',
};

/**
 * Expand abbreviated book names in a reference string.
 * e.g. "Mt 5:3" → "Matthew 5:3", "Jno 3:16" → "John 3:16"
 */
const expandRefAbbrev = (refStr) => {
  if (!refStr) return refStr;
  return refStr?.replace(
    /\b((?:[123]\s)?[A-Z][a-z]*)\s+(\d+:\d+(?:-\d+)?)\b/g,
    (match, bookPart, ref) => {
      const expanded = BOOK_ABBREV_INLINE?.[bookPart];
      return expanded ? `${expanded} ${ref}` : match;
    }
  );
};

// Render inline Bible references as clickable links within a text string
const renderInlineRefs = (text, keyPrefix, onVerseReference) => {
  if (!text || !onVerseReference) return text;

  // Expand abbreviations first
  const expandedText = expandRefAbbrev(text);

  const refPattern = /\b((?:[123]\s)?[A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s+(\d+):(\d+)(?:-(\d+))?\b/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = refPattern?.exec(expandedText)) !== null) {
    if (match?.index > lastIndex) {
      parts?.push(expandedText?.slice(lastIndex, match?.index));
    }
    const [fullMatch, bookName, chap, startVerse, endVerse] = match;
    const ref = endVerse ? `${bookName} ${chap}:${startVerse}-${endVerse}` : `${bookName} ${chap}:${startVerse}`;
    parts?.push(
      <button
        key={`${keyPrefix}-ref-${match?.index}`}
        onClick={() => onVerseReference?.(ref)}
        className="text-amber-500 hover:text-amber-600 underline font-medium transition-colors"
        title={`Go to ${ref}`}
      >
        {fullMatch}
      </button>
    );
    lastIndex = match?.index + fullMatch?.length;
  }

  if (lastIndex < expandedText?.length) {
    parts?.push(expandedText?.slice(lastIndex));
  }

  return parts?.length > 0 ? parts : expandedText;
};

// Parse commentary HTML/text and render with paragraph breaks and clickable references
const renderCommentaryContent = (rawText, onVerseReference, textSizePx) => {
  if (!rawText) return null;

  let text = decodeHTMLEntities(rawText);
  // Expand abbreviations in the raw text before further processing
  text = expandRefAbbrev(text);
  text = text?.replace(/<\/p>/gi, '\n')?.replace(/<p[^>]*>/gi, '\n')?.replace(/<br\s*\/?>/gi, '\n');

  const refs = [];
  text = text?.replace(/<ref\s+target="([^"]+)"[^>]*>(.*?)<\/ref>/gi, (match, target, refText) => {
    const idx = refs?.length;
    refs?.push({ target, refText: decodeHTMLEntities(refText) });
    return `__REF_${idx}__`;
  });

  text = text?.replace(/<ref[^>]*>(.*?)<\/ref>/gi, (match, refText) => {
    const decoded = decodeHTMLEntities(refText);
    const idx = refs?.length;
    refs?.push({ target: null, refText: decoded, parsedRef: decoded });
    return `__REF_${idx}__`;
  });

  text = text?.replace(/<[^>]+>/g, '');

  const paragraphs = text?.split(/\n+/)?.map(p => p?.trim())?.filter(p => p?.length > 0);

  return (
    <div className="space-y-2">
      {paragraphs?.map((para, pIdx) => {
        const parts = para?.split(/(__REF_\d+__)/);
        const rendered = parts?.map((part, partIdx) => {
          const refMatch = part?.match(/^__REF_(\d+)__$/);
          if (refMatch) {
            const ref = refs?.[parseInt(refMatch?.[1])];
            if (!ref) return part;

            if (ref?.target) {
              const targetParts = ref?.target?.split('/');
              const bookSlug = targetParts?.[0];
              const verseRef = targetParts?.[1];
              const [chapStr, verseStr] = (verseRef || '')?.split('-');
              const displayRef = `${bookSlug?.replace(/_/g, ' ')} ${chapStr}:${verseStr}`;
              return (
                <button
                  key={`${pIdx}-${partIdx}`}
                  onClick={() => onVerseReference?.(displayRef)}
                  className="text-amber-500 hover:text-amber-600 underline font-medium transition-colors"
                  title={`Go to ${displayRef}`}
                >
                  {ref?.refText}
                </button>
              );
            } else if (ref?.parsedRef) {
              return (
                <button
                  key={`${pIdx}-${partIdx}`}
                  onClick={() => onVerseReference?.(ref?.parsedRef)}
                  className="text-amber-500 hover:text-amber-600 underline font-medium transition-colors"
                  title={`Go to ${ref?.parsedRef}`}
                >
                  {ref?.refText}
                </button>
              );
            }
            return ref?.refText;
          }
          return renderInlineRefs(part, `${pIdx}-${partIdx}`, onVerseReference);
        });
        return (
          <p key={pIdx} className="text-foreground leading-relaxed" style={{ fontSize: textSizePx || '15px', lineHeight: '1.6' }}>
            {rendered}
          </p>
        );
      })}
    </div>
  );
};

const CommentariesVerseInsightsInner = ({ book, chapter, verse, onClose, onVerseReference, textSizePx }) => {
  const [availableCommentators, setAvailableCommentators] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityChecked, setAvailabilityChecked] = useState(false);

  const [selectedCommentator, setSelectedCommentator] = useState(null);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [selectedScope, setSelectedScope] = useState('verse');
  const [contentCache, setContentCache] = useState({});
  const [loadingContent, setLoadingContent] = useState({});

  // Reset when verse changes
  useEffect(() => {
    setAvailableCommentators({});
    setSelectedCommentator(null);
    setAvailableScopes([]);
    setContentCache({});
    setCheckingAvailability(false);
    setAvailabilityChecked(false);
  }, [book, chapter, verse]);

  // Auto-check availability immediately on mount (no accordion gate)
  useEffect(() => {
    if (book && chapter && verse && !availabilityChecked && !checkingAvailability) {
      checkAvailability();
    }
  }, [book, chapter, verse, availabilityChecked, checkingAvailability]);

  const checkAvailability = async () => {
    setCheckingAvailability(true);

    try {
      const bookNameFormatted = book?.charAt(0)?.toUpperCase() + book?.slice(1);
      const chapterInt = parseInt(chapter);
      const verseInt = parseInt(verse);

      const { data, error } = await supabase
        ?.from('bible_commentaries')
        ?.select('commentator_id, chapter, verse')
        ?.eq('book_name', bookNameFormatted)
        ?.in('chapter', [0, chapterInt])
        ?.in('verse', [0, verseInt]);

      if (error) {
        console.error('Error checking commentary availability:', error);
        return;
      }

      const commentatorScopeMap = {};
      data?.forEach((row) => {
        const cid = row?.commentator_id;
        if (!commentatorScopeMap?.[cid]) {
          commentatorScopeMap[cid] = { verse: false, chapter: false, book: false };
        }
        const rowChapter = parseInt(row?.chapter);
        const rowVerse = parseInt(row?.verse);
        if (rowChapter === chapterInt && rowVerse === verseInt) {
          commentatorScopeMap[cid].verse = true;
        } else if (rowChapter === chapterInt && rowVerse === 0) {
          commentatorScopeMap[cid].chapter = true;
        } else if (rowChapter === 0 && rowVerse === 0) {
          commentatorScopeMap[cid].book = true;
        }
      });

      setAvailableCommentators(commentatorScopeMap);
      setAvailabilityChecked(true);
    } catch (err) {
      console.error('Error checking commentary availability:', err);
    } finally {
      setCheckingAvailability(false);
    }
  };

  const handleSelectCommentator = (commentatorId) => {
    if (selectedCommentator === commentatorId) {
      setSelectedCommentator(null);
      setAvailableScopes([]);
      return;
    }
    setSelectedCommentator(commentatorId);
    const scopes = availableCommentators?.[commentatorId] || {};
    const availScopeList = SCOPES?.filter((s) => scopes?.[s?.id]);
    setAvailableScopes(availScopeList);
    const defaultScope = scopes?.verse ? 'verse' : availScopeList?.[0]?.id || 'verse';
    setSelectedScope(defaultScope);
  };

  const loadContent = useCallback(
    async (commentatorId, scope) => {
      if (contentCache?.[commentatorId]?.[scope] !== undefined) return;

      const cacheKey = `${commentatorId}-${scope}`;
      setLoadingContent((prev) => ({ ...prev, [cacheKey]: true }));

      try {
        const bookNameFormatted = book?.charAt(0)?.toUpperCase() + book?.slice(1);
        let queryChapter, queryVerse;

        if (scope === 'verse') {
          queryChapter = parseInt(chapter);
          queryVerse = parseInt(verse);
        } else if (scope === 'chapter') {
          queryChapter = parseInt(chapter);
          queryVerse = 0;
        } else if (scope === 'book') {
          queryChapter = 0;
          queryVerse = 0;
        }

        const { data, error } = await supabase?.from('bible_commentaries')?.select('commentary_text')?.eq('commentator_id', commentatorId)?.eq('book_name', bookNameFormatted)?.eq('chapter', queryChapter)?.eq('verse', queryVerse)?.maybeSingle();

        if (error) throw error;

        const content = data?.commentary_text || null;

        setContentCache((prev) => ({
          ...prev,
          [commentatorId]: {
            ...prev?.[commentatorId],
            [scope]: content,
          },
        }));
      } catch (err) {
        console.error(`Error loading ${scope} commentary for ${commentatorId}:`, err);
        setContentCache((prev) => ({
          ...prev,
          [commentatorId]: {
            ...prev?.[commentatorId],
            [scope]: null,
          },
        }));
      } finally {
        setLoadingContent((prev) => ({ ...prev, [cacheKey]: false }));
      }
    },
    [book, chapter, verse, contentCache]
  );

  useEffect(() => {
    if (selectedCommentator && selectedScope) {
      loadContent(selectedCommentator, selectedScope);
    }
  }, [selectedCommentator, selectedScope]);

  const currentContent = selectedCommentator ? contentCache?.[selectedCommentator]?.[selectedScope] : null;
  const currentLoadingKey = `${selectedCommentator}-${selectedScope}`;
  const isLoadingContent = loadingContent?.[currentLoadingKey];

  const getScopeLabel = (scopeId) => {
    const bookTitle = book ? book?.charAt(0)?.toUpperCase() + book?.slice(1) : '';
    if (scopeId === 'book') return bookTitle;
    if (scopeId === 'chapter') return `Chapter ${chapter}`;
    return `Verse ${verse}`;
  };

  // Show skeleton while checking availability
  if (checkingAvailability) {
    return <CommentaryPanelSkeleton />;
  }

  return (
    <div className="mt-3 bg-amber-500/5 border border-amber-500/20 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-amber-500/10">
        <div className="flex items-center gap-2">
          <Icon name="BookText" size={15} className="text-amber-500" />
          <span className="font-semibold text-foreground" style={{ fontSize: textSizePx || '13px' }}>Commentaries</span>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground h-6 w-6 p-0"
        >
          <Icon name="X" size={14} />
        </Button>
      </div>

      {/* Body — always visible */}
      <div className="p-3 space-y-3">
        {/* Commentator name pills */}
        <div className="flex flex-wrap gap-1.5">
          {COMMENTATORS?.map((commentator) => {
            const hasContent = !!availableCommentators?.[commentator?.id];
            const isSelected = selectedCommentator === commentator?.id;

            return (
              <button
                key={commentator?.id}
                onClick={() => hasContent && handleSelectCommentator(commentator?.id)}
                disabled={availabilityChecked && !hasContent}
                style={{ fontSize: textSizePx || '13px' }}
                className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                  isSelected
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : hasContent
                    ? 'bg-muted text-foreground border border-border hover:bg-muted/80 cursor-pointer'
                    : 'bg-muted/50 text-muted-foreground border border-border cursor-not-allowed opacity-50'
                }`}
              >
                {commentator?.label}
              </button>
            );
          })}
        </div>

        {/* Scope tabs */}
        {selectedCommentator && availableScopes?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-t border-border pt-2">
            {availableScopes?.map((scope) => (
              <button
                key={scope?.id}
                onClick={() => setSelectedScope(scope?.id)}
                style={{ fontSize: textSizePx || '13px' }}
                className={`px-3 py-1 rounded-md font-medium transition-colors ${
                  selectedScope === scope?.id
                    ? 'bg-primary text-primary-foreground' :'bg-muted text-muted-foreground border border-border hover:bg-muted/80'
                }`}
              >
                {getScopeLabel(scope?.id)}
              </button>
            ))}
          </div>
        )}

        {/* Commentary content */}
        {selectedCommentator && selectedScope && (
          <div className="bg-card border border-border rounded-lg p-3 min-h-[80px]">
            {isLoadingContent ? (
              <div className="flex items-center justify-center py-6 gap-2 text-amber-500">
                <Icon name="Loader2" size={15} className="animate-spin" />
                <span style={{ fontSize: textSizePx || '13px' }}>Loading commentary...</span>
              </div>
            ) : currentContent ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-amber-500" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 1px)` : '12px' }}>
                    {COMMENTATORS?.find((c) => c?.id === selectedCommentator)?.label}
                  </span>
                  <span className="text-muted-foreground" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 1px)` : '12px' }}>—</span>
                  <span className="text-muted-foreground" style={{ fontSize: textSizePx ? `calc(${textSizePx} - 1px)` : '12px' }}>
                    {getScopeLabel(selectedScope)}
                  </span>
                </div>
                <div style={textSizePx ? { fontSize: textSizePx, lineHeight: '1.6' } : {}}>
                  {renderCommentaryContent(currentContent, onVerseReference, textSizePx)}
                </div>
              </div>
            ) : currentContent === null && !isLoadingContent ? (
              <div className="flex items-center justify-center py-4 text-muted-foreground">
                <Icon name="FileX" size={16} className="mr-2" />
                <span style={{ fontSize: textSizePx || '13px' }}>No content available for this selection.</span>
              </div>
            ) : null}
          </div>
        )}

        {/* No commentaries found */}
        {availabilityChecked && Object.keys(availableCommentators)?.length === 0 && (
          <div className="text-center py-4 text-muted-foreground">
            <Icon name="BookOpen" size={20} className="mx-auto mb-2 opacity-60" />
            <p style={{ fontSize: textSizePx || '13px' }}>No commentaries found for this verse.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const CommentariesVerseInsights = (props) => (
  <CommentariesErrorBoundary>
    <CommentariesVerseInsightsInner {...props} />
  </CommentariesErrorBoundary>
);

export default CommentariesVerseInsights;
